import axios from "axios";
import { Colour } from "../data-models/colour";
import { Inventory } from "../data-models/inventory";
import { Project } from "../data-models/project";
import { ProjectCompleteThread } from "../data-models/project-complete-thread";
import { PurchaseThread } from "../data-models/purchase-thread";
import { ShoppingThread } from "../data-models/shopping-thread";
import { Token } from "../data-models/token";
import { User } from "../data-models/user";

export class DataService {
    readonly urlRoot = process.env.REACT_APP_API_ROOT_URL;   

    async login(userName: string, password: string) : Promise<User> {
        console.log("DataService:login");
        const url = this.urlRoot + "users/login";
        const loginData = {
            userName: userName,
            password: password
        }
        const response = await axios.post(url, loginData);
        return response.data;
    }

    async getAccessToken(user:User) : Promise<Token> {
        console.log("DataService:getAccessToken");
        const url = this.urlRoot + "auth/token";
        const tokenRequest = {
            userId: user.id,
            refreshToken: user.refreshToken
        }
        const response = await axios.post(url, tokenRequest);
        user.refreshToken = response.data.refreshToken;
        return response.data;
    }

    async getInventory(userId: string, accessToken: string) : Promise<Inventory[]> {
        console.log("DataService:getInventory");
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const url = this.urlRoot + "users/" + userId + "/inventory";
        const inventory = await axios.get(url, bearerToken);
        return inventory.data;
    }

    async addInventory(userId: string, accessToken: string, inventory: Inventory) : Promise<Inventory> {
        console.log("DataService:addInventory");
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const url = this.urlRoot + "users/" + userId + "/inventory";
        const response = await axios.post(url, inventory, bearerToken);
        return response.data;
    }

    async updateInventory(userId: string, accessToken: string, inventory: Inventory): Promise<Inventory> {
        console.log("DataService:updateInventory");
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const url = this.urlRoot + "users/" + userId + "/inventory/" + inventory.id;
        const response = await axios.put(url, inventory, bearerToken);
        return response.data;
    }

    async deleteInventory(userId: string, accessToken: string, inventoryId: number): Promise<void> {
        console.log("DataService:deleteInventory");
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const url = this.urlRoot + "users/" + userId + "/inventory/" + inventoryId;
        await axios.delete(url,  bearerToken);
        return;
    }

    async updateInventoryBobbin(userId: string, accessToken: string, inventoryId: number, isOnBobbin: boolean):Promise<void> {
        console.log("DataService:updateInventoryBobbin");
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const body = {
            isOnBobbin: isOnBobbin
        };
        const url = this.urlRoot + "users/" + userId + "/inventory/" + inventoryId + "/bobbin";
        await axios.patch(url, body, bearerToken);
        return;
       
    }

    async getProjects(userId: string, accessToken: string) : Promise<Project[]> {
        console.log("DataService:getProjects");
        const url = this.urlRoot + userId  + "/projects";
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const projects = await axios.get(url, bearerToken);
        return projects.data;
    }

    async getProject(userId: string, accessToken: string, projectId:number) : Promise<Project> {
        console.log("DataService:getProject");
        const url = this.urlRoot + "users/" + userId + "/projects/" + projectId;
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const project = await axios.get(url, bearerToken);
        return project.data;
    }

    async saveProject(userId: string, accessToken: string, project:Project): Promise<void> {
        console.log("DataService:saveProject");
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        if(project.id > 0){
            const url = this.urlRoot + "users/" + userId + "/projects/" + project.id;
            const response = await axios.put(url, project, bearerToken);
        }
        else {
            const url = this.urlRoot + "users/" + userId + "/projects";
            const response = await axios.post(url, project, bearerToken);
        }
        return;
    }

    async startProject(userId: string, accessToken: string, project:Project):Promise<void> {
        console.log("DataService:startProject");
        const url = this.urlRoot + "users/" + userId + "/projects/" + project.id + "/start";
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        const threads = project.threads.map(t => {
            return {
                id: t.id,
                colourNumber: t.colourNumber, 
                meters: t.meters,
                assignedMeters: t.assignedMeters,
                complete: false
            }
        })
        await axios.post(url, threads, bearerToken);
    }

    async completeProject(userId: string, accessToken: string, projectId: number, returnedThreads: ProjectCompleteThread[]):Promise<void> {
        console.log("DataService:completeProject");
        const url = this.urlRoot + "users/" + userId + "/projects/" + projectId + "/complete";
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        await axios.post(url, returnedThreads, bearerToken);
    }

    async getColours() : Promise<Colour[]> {
        console.log("DataService:getColours");
        const url = this.urlRoot + "colours";
        const colours = await axios.get(url);
        return colours.data;
    }

    async getShoppingList(userId: string, accessToken: string, projects:number[]) : Promise<ShoppingThread[]> {
        console.log("DataService:getShoppingList");
        var url = this.urlRoot + "users/" + userId + "/shopping/list";
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        if(projects.length > 0 )
        {
            url = url.concat("?");
            projects.forEach(p => url = url.concat("projectIds=" + p + "&"));
            url = url.slice(0,-1);
        }

        const list = await axios.get(url, bearerToken);
        return list.data;
    }

    async purchaseThreads(userId: string, accessToken: string, threads:PurchaseThread[]) : Promise<void> {
        console.log("DataService:purchaseThreads");
        const url = this.urlRoot + "users/" + userId + "/shopping/purchase";
        const bearerToken = {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        }
        await axios.post(url, threads, bearerToken);
        return;
    }


}